import React, { useState, useRef } from 'react';
import { Menu, X, ChevronDown, Sparkles, Globe, ShoppingCart } from 'lucide-react';
import { Button } from '../ui/Button';
import { useLanguage } from '../../i18n/LanguageContext';
import { useCart } from '../../contexts/CartContext';
import { useUser } from '../../contexts/UserContext';
import { useClickOutside } from './header/useClickOutside';
import { DesktopNav } from './header/DesktopNav';
import { CartDropdown } from './header/CartDropdown';
import { ProfileDropdown } from './header/ProfileDropdown';
import { LanguageDropdown } from './header/LanguageDropdown';
import { MobileMenu } from './header/MobileMenu';
interface HeaderProps {
  onNavigate: (page: string) => void;
  currentPage: string;
}
function AstriqoLogo() {
  return <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-17320">
      {/* Линии созвездия - буква А */}
      <line x1="6" y1="30" x2="18" y2="4" stroke="url(#goldGrad)" strokeWidth="1.8" strokeLinecap="round" data-id="element-17321" />
      <line x1="18" y1="4" x2="30" y2="30" stroke="url(#navyGrad)" strokeWidth="1.8" strokeLinecap="round" data-id="element-17322" />
      <line x1="10" y1="21" x2="26" y2="21" stroke="url(#goldGrad)" strokeWidth="1.5" strokeLinecap="round" data-id="element-17323" />
      {/* Звёзды в узлах */}
      <path d="M18 1 L19.2 3.6 L22 3.6 L19.9 5.4 L20.7 8 L18 6.4 L15.3 8 L16.1 5.4 L14 3.6 L16.8 3.6 Z" fill="#C9A84C" data-id="element-17324" />
      <path d="M6 27.2 L6.9 29.3 L9.2 29.3 L7.5 30.7 L8.1 32.8 L6 31.6 L3.9 32.8 L4.5 30.7 L2.8 29.3 L5.1 29.3 Z" fill="#0F1F5C" data-id="element-17325" />
      <path d="M30 27.2 L30.9 29.3 L33.2 29.3 L31.5 30.7 L32.1 32.8 L30 31.6 L27.9 32.8 L28.5 30.7 L26.8 29.3 L29.1 29.3 Z" fill="#C9A84C" data-id="element-17326" />
      <path d="M10 18.5 L10.8 20.3 L12.8 20.3 L11.3 21.5 L11.9 23.3 L10 22.2 L8.1 23.3 L8.7 21.5 L7.2 20.3 L9.2 20.3 Z" fill="#0F1F5C" data-id="element-17327" />
      <path d="M26 18.5 L26.8 20.3 L28.8 20.3 L27.3 21.5 L27.9 23.3 L26 22.2 L24.1 23.3 L24.7 21.5 L23.2 20.3 L25.2 20.3 Z" fill="#0F1F5C" data-id="element-17328" />
      <defs data-id="element-17329">
        <linearGradient id="goldGrad" x1="0" y1="0" x2="1" y2="1" data-id="element-17330">
          <stop offset="0%" stopColor="#C9A84C" data-id="element-17331" />
          <stop offset="100%" stopColor="#E8D5A3" data-id="element-17332" />
        </linearGradient>
        <linearGradient id="navyGrad" x1="0" y1="0" x2="1" y2="1" data-id="element-17333">
          <stop offset="0%" stopColor="#0F1F5C" data-id="element-17334" />
          <stop offset="100%" stopColor="#1a3a8f" data-id="element-17335" />
        </linearGradient>
      </defs>
    </svg>;
}
export function Header({
  onNavigate,
  currentPage
}: HeaderProps) {
  const {
    t,
    language,
    setLanguage,
    languages: langOptions
  } = useLanguage();
  const {
    items,
    removeItem
  } = useCart();
  const {
    user,
    isLoggedIn,
    logout
  } = useUser();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const cartRef = useRef<HTMLDivElement>(null);
  useClickOutside(profileRef, () => setIsProfileOpen(false));
  useClickOutside(langRef, () => setIsLangOpen(false));
  useClickOutside(cartRef, () => setIsCartOpen(false));
  const formatPrice = (p: number) => p.toLocaleString('ru-RU') + ' ₽';
  const cartTotal = items.reduce((sum, i) => sum + i.price, 0);
  const handleNavClick = (page: string) => {
    onNavigate(page);
    setIsMobileMenuOpen(false);
    setIsProfileOpen(false);
    setIsLangOpen(false);
    setIsCartOpen(false);
  };
  const currentLang = langOptions.find(l => l.code === language);
  return <header className="sticky top-0 z-50 w-full bg-white/90 backdrop-blur-xl border-b border-slate-200/60 shadow-sm" data-id="element-17336">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-17337">
        <div className="flex items-center justify-between h-16" data-id="element-17338">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center cursor-pointer group" onClick={() => handleNavClick('home')} data-id="element-17339">
            <div className="mr-2.5 group-hover:scale-105 transition-transform duration-200" data-id="element-17340">
              <AstriqoLogo data-id="element-17341" />
            </div>
            <span className="font-bold text-lg text-slate-900 tracking-tight" data-id="element-17342">
              Astriqo
            </span>
          </div>

          {/* Desktop Navigation */}
          <DesktopNav currentPage={currentPage} handleNavClick={handleNavClick} t={t} data-id="element-17343" />

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-2" data-id="element-17344">
            <button onClick={() => handleNavClick('free-chart')} className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-full transition-colors ${currentPage === 'free-chart' ? 'text-white bg-indigo-600 shadow-md' : 'text-indigo-600 bg-indigo-50 hover:bg-indigo-100'}`} data-id="element-17345">
              <Sparkles className="w-3.5 h-3.5" data-id="element-17346" />
              {t('nav.freeChart')}
            </button>

            <div className="relative" ref={langRef} data-id="element-17347">
              <button onClick={() => setIsLangOpen(!isLangOpen)} className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-sm font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors" data-id="element-17348">
                <Globe className="w-4 h-4" data-id="element-17349" />
                <span data-id="element-17350">{currentLang?.flag}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${isLangOpen ? 'rotate-180' : ''}`} data-id="element-17351" />
              </button>
              {isLangOpen && <LanguageDropdown langOptions={langOptions} language={language} setLanguage={code => {
              setLanguage(code);
              setIsLangOpen(false);
            }} data-id="element-17352" />}
            </div>

            <div className="relative" ref={cartRef} data-id="element-17353">
              <button onClick={() => setIsCartOpen(!isCartOpen)} className="relative flex items-center gap-1 px-2.5 py-2 rounded-lg text-sm font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors" data-id="element-17354">
                <ShoppingCart className="w-4 h-4" data-id="element-17355" />
                {items.length > 0 && <span className="absolute -top-0.5 -right-0.5 bg-indigo-600 text-white text-[10px] font-bold w-4.5 h-4.5 flex items-center justify-center rounded-full min-w-[18px] px-1" data-id="element-17356">
                    {items.length}
                  </span>}
              </button>
              {isCartOpen && <CartDropdown items={items} removeItem={removeItem} formatPrice={formatPrice} cartTotal={cartTotal} onNavigate={page => {
              handleNavClick(page);
              setIsCartOpen(false);
            }} setIsCartOpen={setIsCartOpen} data-id="element-17357" />}
            </div>

            {isLoggedIn ? <div className="relative" ref={profileRef} data-id="element-17358">
                <button onClick={() => setIsProfileOpen(!isProfileOpen)} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-100 transition-colors" data-id="element-17359">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold" data-id="element-17360">
                    {user?.name?.charAt(0) || 'U'}
                  </div>
                  <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} data-id="element-17361" />
                </button>
                {isProfileOpen && <ProfileDropdown user={user} onNavigate={page => {
              handleNavClick(page);
              setIsProfileOpen(false);
            }} onLogout={() => {
              logout();
              setIsProfileOpen(false);
              handleNavClick('home');
            }} data-id="element-17362" />}
              </div> : <Button variant="primary" size="sm" className="rounded-full" onClick={() => handleNavClick('login')} data-id="element-17363">
                {t('nav.login')}
              </Button>}
          </div>

          {/* Mobile Menu Toggle */}
          <button className="lg:hidden relative z-50 p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" data-id="element-17364">
            {isMobileMenuOpen ? <X className="w-5 h-5" data-id="element-17365" /> : <Menu className="w-5 h-5" data-id="element-17366" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && <MobileMenu currentPage={currentPage} handleNavClick={handleNavClick} t={t} isLoggedIn={isLoggedIn} user={user} language={language} setLanguage={setLanguage} langOptions={langOptions} onLogout={() => {
      logout();
      setIsMobileMenuOpen(false);
      handleNavClick('home');
    }} data-id="element-17367" />}
    </header>;
}